import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Download, MessageSquare, Bot, Database, ArrowDownToLine, ArrowUpFromLine, IndianRupee, Gauge, FileText, Layers, PieChart } from 'lucide-react';
import api from '../../lib/api';

const inr = (n) => (n == null ? null : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n));
const intIN = (n) => new Intl.NumberFormat('en-IN').format(n || 0);
const compact = (n) => {
  n = n || 0;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return intIN(n);
};
const NT = 'Not tracked';

function Card({ icon: Icon, label, value, sub, testid, highlight }) {
  return (
    <div className={`fab-card p-4 flex flex-col ${highlight ? 'ring-1 ring-fab-primary/40 bg-fab-primary/5' : ''}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-fab-text-muted text-[12px] font-medium">
        <Icon className="w-4 h-4 text-fab-primary" /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-fab-text" data-testid={`${testid}-value`}>{value}</div>
      {sub != null && <div className="mt-1 text-[11px] text-fab-text-muted">{sub}</div>}
    </div>
  );
}

export default function AdminSellerMatrix() {
  const { vid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30');
  const [showAllDays, setShowAllDays] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (parseInt(range, 10) - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    api.get(`/admin/matrix/seller/${vid}`, { params: { date_from: fmt(from), date_to: fmt(to) } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [vid, range]);
  useEffect(load, [load]);

  const exportCsv = () => {
    if (!data) return;
    const head = ['Date', 'Total Messages', 'AI Replies (Answered)', 'Deterministic Replies', 'Blocked Attempts',
      'Seller-Tool Operations', 'Input Tokens', 'Output Tokens', 'Total Tokens', 'Priced Cost (INR)',
      'Unpriced Tokens (in/out)', 'Avg Cost per AI Reply (INR)'];
    const rows = (data.daily || []).map((d) => [
      d.date, d.total_messages, d.ai_replies ?? 0, d.deterministic_replies ?? 0, d.blocked_attempts ?? 0,
      d.seller_tool_calls ?? 0, d.input_tokens, d.output_tokens, d.total_tokens,
      d.total_priced_cost_inr ?? 0, `${d.unpriced_input_tokens ?? 0}/${d.unpriced_output_tokens ?? 0}`,
      d.avg_cost_per_ai_reply == null ? 'Not tracked' : d.avg_cost_per_ai_reply,
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `seller-matrix-${data.seller?.name || vid}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>;
  if (!data) return <div className="fab-card p-6 text-fab-text-muted" data-testid="seller-matrix-empty">Seller Matrix unavailable.</div>;

  const t = data.totals;
  const sel = data.seller;
  const initials = (sel.name || 'S').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const daysShown = showAllDays ? (data.daily || []) : (data.daily || []).slice(0, 5);

  return (
    <div className="space-y-4" data-testid="seller-matrix-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-[12px] text-fab-text-muted" data-testid="seller-matrix-breadcrumb">
        <Link to="/dashboard/admin/vendors" className="hover:text-fab-primary font-semibold">Sellers</Link>
        <span>/</span>
        <Link to={`/dashboard/admin/sellers/${vid}`} className="hover:text-fab-primary font-semibold">{sel.name}</Link>
        <span>/</span>
        <span className="text-fab-text font-semibold">Matrix</span>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fab-text">Seller Matrix</h1>
          <p className="text-[13px] text-fab-text-muted">AI messaging cost &amp; token usage for this seller</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={range} onChange={(e) => setRange(e.target.value)} className="fab-input text-[12px] py-1.5" data-testid="seller-matrix-range">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button onClick={() => navigate(`/dashboard/admin/vendors/${vid}/view`)} className="fab-btn-secondary inline-flex items-center gap-1.5 text-[12px]" data-testid="seller-matrix-back">
            <ArrowLeft className="w-4 h-4" /> Back to Seller View
          </button>
        </div>
      </div>

      {/* Seller identity */}
      <div className="fab-card p-5 flex items-center justify-between flex-wrap gap-3" data-testid="seller-matrix-identity">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-full bg-fab-primary/10 text-fab-primary font-bold flex items-center justify-center">{initials}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-fab-text">{sel.name}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sel.status === 'ACTIVE' ? 'bg-green-50 text-green-700 border border-green-300' : 'bg-red-50 text-red-700 border border-red-300'}`} data-testid="seller-matrix-status">{sel.status}</span>
            </div>
            <div className="text-[12px] text-fab-text-muted">Email: {sel.email || '—'}</div>
            <div className="text-[12px] text-fab-text-muted">Contact: {sel.contact || '—'}</div>
            <div className="text-[12px] text-fab-text-muted">Plan: {sel.plan}</div>
          </div>
        </div>
        <button onClick={exportCsv} className="fab-btn-secondary inline-flex items-center gap-1.5 text-[12px]" data-testid="seller-matrix-export">
          <Download className="w-4 h-4" /> Export
        </button>
      </div>

      {/* Metric cards -- ticket 281089 Stage B3: old "AI Messages"/"Avg. Cost
          per AI Message"/one-Sonnet-rate "Total Messaging Cost" REPLACED (not
          shown alongside). Legacy API fields still returned for other
          consumers, just not rendered here. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Card icon={MessageSquare} label="Total Messages" value={intIN(t.total_messages)} sub="web + WhatsApp" testid="sm-card-total-messages" />
        <Card icon={Bot} label="AI replies (answered)" value={intIN(t.ai_replies || 0)} sub="sales_brain only" testid="sm-card-ai-replies" />
        <Card icon={MessageSquare} label="Deterministic replies" value={intIN(t.deterministic_replies || 0)} sub="Incl. blocked/failed fallback reply" testid="sm-card-deterministic" />
        <Card icon={Database} label="Blocked attempts" value={intIN(t.blocked_attempts || 0)} sub="₹0, failed/blocked" testid="sm-card-blocked" />
        <Card icon={Layers} label="Seller-tool operations" value={intIN(t.seller_tool_calls || 0)} sub="poster/cover/catalog/OCR" testid="sm-card-seller-tools" />
        <Card icon={ArrowDownToLine} label="Input Tokens" value={t.tokens_tracked ? compact(t.input_tokens) : NT} sub={t.total_tokens ? `${Math.round(t.input_tokens / t.total_tokens * 100)}% of total tokens` : ''} testid="sm-card-input-tokens" />
        <Card icon={ArrowUpFromLine} label="Output Tokens" value={t.tokens_tracked ? compact(t.output_tokens) : NT} sub={t.total_tokens ? `${Math.round(t.output_tokens / t.total_tokens * 100)}% of total tokens` : ''} testid="sm-card-output-tokens" />
        <Card icon={Gauge} label="Avg Cost per AI Reply" value={t.avg_cost_per_ai_reply == null ? NT : inr(t.avg_cost_per_ai_reply)} sub="Buyer-chat only" testid="sm-card-avg-cost" highlight />
      </div>
      <p className="text-[11px] text-fab-text-muted">
        Deterministic replies includes the automatic fallback reply sent when an AI call was blocked or failed.
        Reply counts are aggregated across the web chatbox and WhatsApp.
      </p>

      {/* Priced cost + unpriced usage */}
      <div className="grid grid-cols-2 gap-3">
        <Card icon={IndianRupee} label="Total Messaging Cost" value={inr(t.total_priced_cost_inr ?? 0)} sub="Priced per call's own model" testid="sm-card-cost" />
        <Card icon={Database} label="Unpriced usage"
          value={(t.unpriced_input_tokens || t.unpriced_output_tokens) ? `${compact(t.unpriced_input_tokens)} in / ${compact(t.unpriced_output_tokens)} out` : 'None'}
          sub={t.unpriced_models?.length ? `Price unverified: ${t.unpriced_models.join(', ')}` : 'All usage priced'}
          testid="sm-card-unpriced" />
      </div>

      {/* Daily usage */}
      <div className="fab-card p-5" data-testid="seller-matrix-daily">
        <h3 className="font-display text-lg font-bold text-fab-text mb-3">Daily Usage Overview</h3>
        {daysShown.length === 0 ? (
          <div className="text-[13px] text-fab-text-muted py-4">No usage in this range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-fab-text-muted border-b border-fab-border">
                  <th className="py-2 pr-3 font-semibold">DATE</th>
                  <th className="py-2 pr-3 font-semibold">TOTAL MESSAGES</th>
                  <th className="py-2 pr-3 font-semibold">AI REPLIES</th>
                  <th className="py-2 pr-3 font-semibold">DETERMINISTIC</th>
                  <th className="py-2 pr-3 font-semibold">BLOCKED</th>
                  <th className="py-2 pr-3 font-semibold">SELLER-TOOL OPS</th>
                  <th className="py-2 pr-3 font-semibold">INPUT TOKENS</th>
                  <th className="py-2 pr-3 font-semibold">OUTPUT TOKENS</th>
                  <th className="py-2 pr-3 font-semibold">PRICED COST (₹)</th>
                  <th className="py-2 pr-3 font-semibold">AVG. COST / AI REPLY (₹)</th>
                </tr>
              </thead>
              <tbody>
                {daysShown.map((d) => (
                  <tr key={d.date} className="border-b border-fab-border/60" data-testid={`sm-day-${d.date}`}>
                    <td className="py-2 pr-3 font-semibold">{d.date}</td>
                    <td className="py-2 pr-3">{intIN(d.total_messages)}</td>
                    <td className="py-2 pr-3">{intIN(d.ai_replies)}</td>
                    <td className="py-2 pr-3">{intIN(d.deterministic_replies)}</td>
                    <td className="py-2 pr-3">{intIN(d.blocked_attempts)}</td>
                    <td className="py-2 pr-3">{intIN(d.seller_tool_calls)}</td>
                    <td className="py-2 pr-3">{d.tokens_tracked ? compact(d.input_tokens) : NT}</td>
                    <td className="py-2 pr-3">{d.tokens_tracked ? compact(d.output_tokens) : NT}</td>
                    <td className="py-2 pr-3">{inr(d.total_priced_cost_inr ?? 0)}</td>
                    <td className="py-2 pr-3">{d.avg_cost_per_ai_reply == null ? <span className="text-fab-text-muted italic">{NT}</span> : inr(d.avg_cost_per_ai_reply)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(data.daily || []).length > 5 && (
          <div className="text-center mt-3">
            <button onClick={() => setShowAllDays((v) => !v)} className="text-[13px] font-semibold text-fab-primary hover:underline" data-testid="seller-matrix-more-days">
              {showAllDays ? 'Show fewer days' : 'View more daily usage'}
            </button>
          </div>
        )}
      </div>

      {/* Cost summary */}
      <div className="fab-card p-5" data-testid="seller-matrix-summary">
        <h3 className="font-display text-lg font-bold text-fab-text mb-3">Cost Summary <span className="text-[12px] font-normal text-fab-text-muted">(Last {range} days)</span></h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card icon={IndianRupee} label="Total Messaging Cost (priced)" value={inr(t.total_priced_cost_inr ?? 0)} testid="sm-sum-cost" />
          <Card icon={FileText} label="Total Tokens Used" value={t.tokens_tracked ? compact(t.total_tokens) : NT} testid="sm-sum-total-tokens" />
          <Card icon={ArrowDownToLine} label="Input Tokens" value={t.tokens_tracked ? compact(t.input_tokens) : NT} testid="sm-sum-input-tokens" />
          <Card icon={Layers} label="Output Tokens" value={t.tokens_tracked ? compact(t.output_tokens) : NT} testid="sm-sum-output-tokens" />
          <Card icon={PieChart} label="AI Replies / Total Messages" value={t.total_messages ? `${((t.ai_replies / t.total_messages) * 100).toFixed(2)}%` : '0%'} testid="sm-sum-ai-pct" />
        </div>
      </div>

      <p className="text-[11px] text-fab-text-muted text-center">
        Note: Each AI call is priced at its OWN model's published rate (haiku/sonnet), never one flat rate.
        Gemini image-generation usage has no published rate yet and is shown separately as unpriced usage,
        never folded into the cost above. USD→INR {data.pricing.usd_inr}.
      </p>
    </div>
  );
}
